import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { submitContact } from "@/lib/api";
import { TextField, TextAreaField } from "@/components/forms/FormFields";

const initial = { name: "", email: "", message: "" };

export default function ContactForm() {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in name, email, and message.");
            return;
        }
        setLoading(true);
        try {
            await submitContact(form);
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            console.error(err);
            const msg =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Submission failed. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div
                data-testid="contact-form-confirmation"
                className="bg-white border border-slate-200 rounded-2xl p-10 md:p-14 text-center"
            >
                <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="text-emerald-600" size={24} />
                </div>
                <h3 className="font-display mt-6 text-2xl md:text-3xl font-semibold text-slate-900">
                    Thanks — your message has been received.
                </h3>
                <p className="mt-4 text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
                    The 1099er team will review and respond to you directly.
                </p>
            </div>
        );
    }

    return (
        <form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 space-y-6"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <TextField
                    label="Name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    testId="contact-name"
                    placeholder="Jane Doe"
                />
                <TextField
                    label="Email"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    testId="contact-email"
                    placeholder="jane@example.com"
                />
            </div>
            <TextAreaField
                label="Message"
                required
                value={form.message}
                onChange={set("message")}
                testId="contact-message"
                placeholder="How can we help?"
                rows={5}
            />
            <div className="flex justify-end">
                <button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white text-sm font-medium px-6 py-3 rounded-md hover:bg-slate-800 transition-colors disabled:opacity-60"
                >
                    {loading ? "Sending..." : "Send Message"}
                    {!loading && <ArrowRight size={16} />}
                </button>
            </div>
        </form>
    );
}
